import Layout from 'Nori/Layout';
import Hero from 'Custom/Hero';
import Warnings from 'Custom/Warnings';
import RichText from 'Custom/RichText';
import Container from 'Nori/Container';
import { Table } from 'Custom/Table';
import { AlternativeTable } from 'Custom/Table/AlternativeTable';
import HeroQuestion from 'Custom/HeroQuestion';
import {
  SE_TABLE_1,
  SE_TABLE_2,
  SE_TABLE_3,
  CONDITIONS_TABLE,
} from 'Utils/side-effects';
import PLUS_IMAGE from 'Images/misc/plus-sign-desktop.webp';
import BLUE_BG from 'Images/background/blue-bg.png';
import BLUE_BG_MOBILE from 'Images/background/blue-bg-mobile.png';
import SIDE_EFFECTS_TABLE_BG from 'Images/background/side_effects_table.png';
import styles from 'styles/pages/sideEffects.module.scss';
import SideEffectsTable from 'Custom/Table/SideEffectsTable';

const HERO_IMAGE = {
  src: PLUS_IMAGE,
  width: 403,
};

const HERO_IMAGE_MOBILE = {
  src: PLUS_IMAGE,
};

const HEAD = {
  title: 'Side Effects of NEXPLANON® (etonogestrel implant) 68 mg Radiopaque',
  description:
    'Learn about side effects of NEXPLANON® (etonogestrel implant) 68 mg Radiopaque, when to contact your doctor, and if you should avoid using it.',
  keywords:
    'Nexplanon Side Effects, Birth Control Side Effects, Does Nexplanon Cause Weight Gain',
};

export default function SideEffects() {
  return (
    <Layout head={HEAD}>
      <Hero
        autoHeightOnMobile
        className={styles.customHero}
        backgroundImage={BLUE_BG.src}
        mobileBackgroundImage={BLUE_BG_MOBILE.src}
        variant="overlap"
        image={HERO_IMAGE}
        mobileImage={HERO_IMAGE_MOBILE}
        imageBreakpoint="xl"
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Hero"
        data-design-module_name="SIDE EFFECTS OF NEXPLANON"
        data-design-module_no="01"
        data-qa-tag="hero-section"
      >
        <RichText data-qa-tag="hero-content">
          <h4 className={styles.heroBaseHeading} data-qa-tag="heroBaseHeading">
            SIDE EFFECTS & RISKS
          </h4>
          <h2 data-qa-tag="hero-title">
            Learn about common side effects, possible risks of using{' '}
            <span className={styles.h2Header}>NEXPLANON</span>, and information
            on who shouldn't use{' '}
            <span className={styles.h2Header}>NEXPLANON</span>
          </h2>
        </RichText>
      </Hero>
      <section className={styles.sectionCommonSideEffects}>
        <Container
          size="md"
          data-design-visible="visible_vd"
          data-design-type="module"
          data-design-nori_id="Inline Content"
          data-design-module_name="what are the most common side effects of NEXPLANON?"
          data-design-module_no="02"
          data-qa-tag="section-common-side-effects"
        >
          <RichText
            className={styles.tableWrapper}
            data-qa-tag="richtext-common-side-effects"
          >
            <h2 data-qa-tag="heading-common-side-effects">
              What are the most common side effects of NEXPLANON?
            </h2>
            <Table
              className="sm"
              content={SE_TABLE_1}
              title="what are the most common side effects of NEXPLANON?"
              data-qa-tag="table-common-side-effects"
            />
          </RichText>
        </Container>
      </section>

      <Warnings
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="don't use NEXPLANON if you…"
        data-design-module_no="03"
        data-qa-tag="section-warnings"
      />

      <SideEffectsTable
        showIcon={false}
        {...CONDITIONS_TABLE}
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="tell your doctor if you have or have had any of these conditions"
        data-design-module_no="04"
        data-qa-tag="section-conditions-table"
      >
        <RichText data-qa-tag="richtext-conditions-intro">
          <h2 className="mb" data-qa-tag="heading-tell-your-doctor">
            Tell your doctor if you have or have had any of these conditions
          </h2>
          <p className="intro" data-qa-tag="text-suggest-different-method">
            Your doctor can suggest a different method of birth control.
          </p>
        </RichText>
      </SideEffectsTable>

      <div
        className={styles.tableBg}
        style={{ backgroundImage: `url(${SIDE_EFFECTS_TABLE_BG.src})` }}
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="here are some other possible risks with NEXPLANON"
        data-design-module_no="05"
        data-qa-tag="section-other-risks"
      >
        <Container size="md" data-qa-tag="container-other-risks">
          <RichText
            className={styles.tableWrapper}
            data-qa-tag="richtext-other-risks"
          >
            <h2
              className={styles.mediumTitle}
              data-qa-tag="heading-other-risks"
            >
              Here are some other possible risks with NEXPLANON
            </h2>
            <Table
              className="sm"
              content={SE_TABLE_2}
              title="here are some other possible risks with NEXPLANON"
              data-qa-tag="table-other-risks"
            />
          </RichText>
        </Container>
      </div>
      <section className={styles.sectionCallYourDoctor}>
        <Container
          data-design-visible="visible_vd"
          data-design-type="module"
          data-design-nori_id="Inline Content"
          data-design-module_name="call your doctor immediately if you have …"
          data-design-module_no="06"
          data-qa-tag="section-when-to-call"
        >
          <RichText
            className={styles.tableWrapper}
            data-qa-tag="richtext-when-to-call"
          >
            <div className={styles.title} data-qa-tag="div-when-to-call-title">
              <h2 data-qa-tag="heading-when-to-call">
                When should I call my healthcare professional?
              </h2>
              <div
                className={styles.textIntroDesktop}
                data-qa-tag="subheading-call-immediately"
              >
                Call your healthcare professional immediately if you have…
              </div>
            </div>
            <AlternativeTable
              content={SE_TABLE_3}
              className="sm alternative-new-mobile-table"
              title="call your doctor immediately if you have …"
              data-qa-tag="table-when-to-call"
            />
          </RichText>
        </Container>
      </section>

      <HeroQuestion moduleNo="07"></HeroQuestion>
    </Layout>
  );
}
